// Libraries
import _ from 'lodash';
import React, {useState, useEffect} from 'react';

// Supermove
import {Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useLazyQuery} from '@supermove/hooks';

// App
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import {FormikSetupCallrailIntegrationFormType} from '@shared/modules/CallrailIntegration/forms/SetupCallrailIntegrationForm';

interface CallrailAccountAndCompanyFieldsProps {
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
}

const CallrailAccountAndCompanyFields = ({form, field}: CallrailAccountAndCompanyFieldsProps) => {
  const [accountOptions, setAccountOptions] = useState<{label: string; value: string}[]>([]);
  const [companyOptions, setCompanyOptions] = useState<{label: string; value: string}[]>([]);

  // Get the API key from the form
  const callrailApiKey = form.values[field]?.callrailApiKey;

  // Get the selected account ID
  const selectedAccountId = form.values[field]?.callrailAccountId;

  // Query to fetch CallRail accounts
  const [fetchAccounts, {data: accountsData, loading: accountsLoading}] = useLazyQuery(
    CallrailAccountAndCompanyFields.accountsQuery,
    {
      variables: {
        callrailApiKey,
      },
      fetchPolicy: 'network-only',
    },
  );

  // Query to fetch CallRail companies for a selected account
  const [fetchCompanies, {data: companiesData, loading: companiesLoading}] = useLazyQuery(
    CallrailAccountAndCompanyFields.companiesQuery,
    {
      variables: {
        callrailApiKey,
        callrailAccountId: selectedAccountId,
      },
      fetchPolicy: 'network-only',
    },
  );

  // Effect to fetch accounts when API key is available
  useEffect(() => {
    if (callrailApiKey) {
      fetchAccounts();
    }
  }, [callrailApiKey, fetchAccounts]);

  // Effect to fetch companies when account is selected
  useEffect(() => {
    if (callrailApiKey && selectedAccountId) {
      fetchCompanies();
    }
  }, [callrailApiKey, selectedAccountId, fetchCompanies]);

  // Effect to update account options when data is loaded
  useEffect(() => {
    if (accountsData?.callrailAccounts) {
      const options = accountsData.callrailAccounts.map((account: any) => ({
        label: account.accountName,
        value: account.accountId,
      }));
      setAccountOptions(options);
    }
  }, [accountsData]);

  // Effect to update company options when data is loaded
  useEffect(() => {
    if (companiesData?.callrailCompanies) {
      const options = companiesData.callrailCompanies.map((company: any) => ({
        label: company.companyName,
        value: company.companyId,
      }));
      setCompanyOptions(options);
    }
  }, [companiesData]);

  // Handle account selection
  const handleAccountChange = (value: string) => {
    const selectedAccount = _.find(
      accountsData?.callrailAccounts,
      (account) => account.accountId === value,
    );

    if (selectedAccount) {
      form.setFieldValue(`${field}.callrailAccountId`, value);
      form.setFieldValue(`${field}.callrailAccountName`, selectedAccount.accountName);

      // Reset company fields when account changes
      form.setFieldValue(`${field}.callrailCompanyId`, '');
      form.setFieldValue(`${field}.callrailCompanyName`, '');
    }
  };

  // Handle company selection
  const handleCompanyChange = (value: string) => {
    const selectedCompany = _.find(
      companiesData?.callrailCompanies,
      (company) => company.companyId === value,
    );

    if (selectedCompany) {
      form.setFieldValue(`${field}.callrailCompanyId`, value);
      form.setFieldValue(`${field}.callrailCompanyName`, selectedCompany.companyName);
    }
  };

  return (
    <React.Fragment>
      <FieldInput
        {...form}
        index={0}
        component={DropdownInput}
        name={`${field}.callrailAccountId`}
        label={'CallRail Account'}
        isRequired
        isResponsive
        input={{
          options: accountOptions,
          placeholder: 'Select CallRail Account',
          isSearchable: true,
          setFieldValue: form.setFieldValue,
          onChangeValue: (value: string) => handleAccountChange(value),
          style: {flex: 1},
          isPortaled: true,
          isLoading: accountsLoading,
        }}
      />
      <Space height={16} />

      {selectedAccountId && (
        <React.Fragment>
          <FieldInput
            {...form}
            index={1}
            component={DropdownInput}
            name={`${field}.callrailCompanyId`}
            label={'CallRail Company'}
            isRequired
            isResponsive
            input={{
              options: companyOptions,
              placeholder: 'Select CallRail Company',
              isSearchable: true,
              setFieldValue: form.setFieldValue,
              onChangeValue: (value: string) => handleCompanyChange(value),
              style: {
                flex: 1,
              },
              isPortaled: true,
              isLoading: companiesLoading,
            }}
          />
          <Space height={16} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CallrailAccountAndCompanyFields.accountsQuery = gql`
  query CallrailAccounts($callrailApiKey: String!) {
    callrailAccounts(callrailApiKey: $callrailApiKey) {
      accountId
      accountName
    }
  }
`;

CallrailAccountAndCompanyFields.companiesQuery = gql`
  query CallrailCompanies($callrailApiKey: String!, $callrailAccountId: String!) {
    callrailCompanies(callrailApiKey: $callrailApiKey, callrailAccountId: $callrailAccountId) {
      companyId
      companyName
    }
  }
`;

export default CallrailAccountAndCompanyFields;
